/**
 * Quiz Mode screen. Setup (scope + difficulty + count), a silent one-at-a-time
 * run with no feedback, then a results screen with overall score, per-topic and
 * per-subskill breakdowns, and a missed-question review with worked solutions.
 */

import type { Screen } from "../router";
import type { Store } from "../state";
import { DIFFICULTY_LABELS } from "../state";
import { el, clear } from "../../ui/dom";
import { mathElement } from "../../ui/math-display";
import { createAnswerInput, type AnswerInput } from "../../ui/answer-input";
import { plotElement } from "../../graph/svg-render";
import { DIFFICULTIES, type Difficulty } from "../../topics/types";
import { UNITS, type TopicId } from "../../curriculum/curriculum";
import { subskillLabel } from "../../curriculum/subskills";
import { createTopicSelect } from "../../ui/topic-select";
import { QuizSession, type QuizResults } from "../quiz-session";
import { eligibleTopics, type QuizScope } from "../question-select";
import { recordQuizSummary } from "../../progress/record";

const COUNTS = [5, 10, 15, 20];

export const quizScreen: Screen = (store, mount) => {
  // A caller (e.g. the Progress "Recommended practice" button) can hand us a
  // ready-made scope to start immediately, skipping the setup form.
  const auto = store.get().screenParams["autostart"];
  if (isAutostart(auto)) {
    const session = new QuizSession(
      { scope: auto.scope, difficulty: auto.difficulty, count: auto.count },
      store.taughtSet(),
    );
    renderActive(store, mount, session);
    return;
  }
  renderSetup(store, mount);
};

interface Autostart {
  scope: QuizScope;
  difficulty: Difficulty;
  count: number;
}

function isAutostart(v: unknown): v is Autostart {
  return (
    !!v &&
    typeof v === "object" &&
    "scope" in v &&
    "difficulty" in v &&
    "count" in v
  );
}

function renderSetup(store: Store, mount: HTMLElement): void {
  clear(mount);
  const taught = store.taughtSet();

  let selectedTopic: TopicId = store.get().settings.currentTopic;
  let scope: QuizScope = { kind: "topic", topic: selectedTopic };
  let difficulty: Difficulty = "standard";
  let count = 10;

  mount.appendChild(el("h1", {}, "Quiz"));

  // A topic dropdown drives the "Single Topic" scope so any topic can be quizzed
  // without changing the Current Topic on Home first. The review chips
  // (unit / first 9 weeks) are the mixed scopes.
  const topicSelect = createTopicSelect({
    selected: selectedTopic,
    ariaLabel: "Quiz topic",
    onChange: (value) => {
      selectedTopic = value;
      scope = { kind: "topic", topic: value };
      store.setCurrentTopic(value);
      // Selecting a topic implies the Single Topic scope.
      const chip = mount.querySelector('[data-scope-kind="topic"]');
      if (chip) syncPressed(chip as HTMLElement, "scope");
    },
  });

  // Scope options: single topic (via the dropdown), each unit, first 9 weeks.
  const scopeOptions: Array<{ label: string; make: () => QuizScope; kind: string }> = [
    { label: "Single Topic", make: () => ({ kind: "topic", topic: selectedTopic }), kind: "topic" },
  ];
  for (const unit of UNITS) {
    if (eligibleTopics({ kind: "unit", unit: unit.id }, taught).length > 0) {
      scopeOptions.push({
        label: `${unit.name} Review`,
        make: () => ({ kind: "unit", unit: unit.id }),
        kind: `unit-${unit.id}`,
      });
    }
  }
  if (eligibleTopics({ kind: "first9" }, taught).length > 0) {
    scopeOptions.push({
      label: "All Topics Review",
      make: () => ({ kind: "first9" }),
      kind: "first9",
    });
  }

  const scopeChips = scopeOptions.map((opt, i) =>
    el(
      "button",
      {
        class: "chip",
        "aria-pressed": i === 0,
        "data-group": "scope",
        "data-scope-kind": opt.kind,
        onClick: (e) => {
          scope = opt.make();
          syncPressed(e.currentTarget as HTMLElement, "scope");
        },
      },
      opt.label,
    ),
  );

  const diffChips = DIFFICULTIES.map((d) =>
    el(
      "button",
      {
        class: "chip",
        "aria-pressed": d === difficulty,
        "data-group": "difficulty",
        onClick: (e) => {
          difficulty = d;
          syncPressed(e.currentTarget as HTMLElement, "difficulty");
        },
      },
      DIFFICULTY_LABELS[d],
    ),
  );

  const countChips = COUNTS.map((c) =>
    el(
      "button",
      {
        class: "chip",
        "aria-pressed": c === count,
        "data-group": "count",
        onClick: (e) => {
          count = c;
          syncPressed(e.currentTarget as HTMLElement, "count");
        },
      },
      String(c),
    ),
  );

  mount.appendChild(
    el(
      "section",
      { class: "card" },
      el("p", { class: "card-title" }, "Quiz Scope"),
      el("div", { class: "options" }, ...scopeChips),
      el(
        "p",
        { class: "muted", style: "margin:0.6rem 0 0.2rem;font-size:0.85rem" },
        "Topic (for Single Topic scope):",
      ),
      topicSelect,
      el("p", { class: "card-title", style: "margin-top:1rem" }, "Difficulty"),
      el("div", { class: "options" }, ...diffChips),
      el("p", { class: "card-title", style: "margin-top:1rem" }, "Number of Questions"),
      el("div", { class: "options" }, ...countChips),
      el(
        "div",
        { class: "btn-row" },
        el(
          "button",
          {
            class: "btn btn-primary",
            onClick: () => {
              const session = new QuizSession(
                { scope, difficulty, count },
                store.taughtSet(),
              );
              renderActive(store, mount, session);
            },
          },
          "Start Quiz",
        ),
      ),
    ),
  );
}

function syncPressed(clicked: HTMLElement, group: string): void {
  const root = clicked.closest("section");
  if (!root) return;
  root
    .querySelectorAll(`[data-group="${group}"]`)
    .forEach((n) => n.setAttribute("aria-pressed", "false"));
  clicked.setAttribute("aria-pressed", "true");
}

function renderActive(
  store: Store,
  mount: HTMLElement,
  session: QuizSession,
): void {
  clear(mount);

  const meta = el("p", { class: "question-meta" });
  const promptWrap = el("div", { class: "question-prompt" });
  const inputHost = el("div", {});
  const controls = el("div", { class: "btn-row" });
  const card = el(
    "section",
    { class: "card" },
    meta,
    promptWrap,
    inputHost,
    el(
      "p",
      { class: "muted", style: "margin-top:0.75rem;font-size:0.85rem" },
      "No feedback until the quiz is finished.",
    ),
    controls,
  );

  mount.appendChild(el("h1", {}, "Quiz"));
  mount.appendChild(card);

  let answerInput: AnswerInput;

  const renderQuestion = (): void => {
    const q = session.current;
    meta.textContent = `Question ${session.position} of ${session.total}`;
    clear(promptWrap);
    promptWrap.appendChild(mathElement(q.prompt.latex ?? q.prompt.text));
    if (q.graph) promptWrap.appendChild(plotElement(q.graph));
    clear(inputHost);
    answerInput = createAnswerInput(q, {});
    inputHost.appendChild(answerInput.element);
    clear(controls);
    controls.appendChild(
      el(
        "button",
        { class: "btn btn-primary", onClick: advance },
        session.isLast ? "Finish Quiz" : "Next",
      ),
    );
    answerInput.focus();
  };

  function advance(): void {
    session.record(answerInput.getValue());
    if (session.isLast) {
      const results = session.finish();
      recordQuizSummary(results);
      renderResults(store, mount, results);
    } else {
      session.next();
      renderQuestion();
    }
  }

  renderQuestion();
}

function renderResults(
  store: Store,
  mount: HTMLElement,
  results: QuizResults,
): void {
  clear(mount);

  mount.appendChild(el("h1", {}, "Quiz Results"));

  // Everything inside `printable` is what a print/save-to-PDF captures; the nav
  // and action buttons are hidden by the print stylesheet.
  const printable = el("div", { class: "printable" });
  mount.appendChild(printable);

  // A heading that only shows on paper, so the printout is self-identifying.
  printable.appendChild(
    el("p", { class: "print-only print-heading" }, `AXLE - ${results.label}`),
  );

  printable.appendChild(
    el(
      "section",
      { class: "card center" },
      el("div", { class: "muted" }, results.label),
      el("div", { class: "stat-value" }, `${results.correct} / ${results.total}`),
      el("div", { class: "muted" }, `${results.percent}%`),
    ),
  );

  // Subskill breakdown (the "underlying skill" view).
  const skillRows: HTMLElement[] = [];
  for (const [sk, tally] of results.bySubskill) {
    skillRows.push(
      el(
        "li",
        { class: "topic-item" },
        el("span", { class: "topic-name" }, subskillLabel(sk)),
        el("span", { class: "badge" }, `${tally.correct}/${tally.total}`),
      ),
    );
  }
  if (skillRows.length > 0) {
    printable.appendChild(
      el(
        "section",
        { class: "card" },
        el("p", { class: "card-title" }, "By Skill"),
        el("ul", { class: "topic-list" }, ...skillRows),
      ),
    );
  }

  // Missed-question review with worked solutions.
  if (results.missed.length > 0) {
    const reviews: HTMLElement[] = [];
    for (const item of results.missed) {
      const q = item.question;
      const block = el(
        "div",
        { class: "reveal", style: "margin-top:0.75rem" },
        el("div", { class: "question-prompt", style: "font-size:1.1rem" }),
      );
      (block.firstChild as HTMLElement).appendChild(
        mathElement(q.prompt.latex ?? q.prompt.text),
      );
      if (q.graph) (block.firstChild as HTMLElement).appendChild(plotElement(q.graph));
      block.appendChild(
        el(
          "p",
          { class: "muted" },
          `Your answer: ${item.raw.trim() === "" ? "(blank)" : item.raw}`,
        ),
      );
      block.appendChild(el("strong", {}, "Worked solution"));
      for (const step of q.steps) {
        const s = el("div", { class: "reveal-step" }, el("div", {}, step.description));
        s.appendChild(mathElement(step.latex));
        block.appendChild(s);
      }
      reviews.push(block);
    }
    printable.appendChild(
      el(
        "section",
        { class: "card" },
        el("p", { class: "card-title" }, `Review (${results.missed.length} missed)`),
        ...reviews,
      ),
    );
  } else {
    printable.appendChild(
      el(
        "section",
        { class: "card center" },
        el("p", {}, "Perfect score - nothing to review."),
      ),
    );
  }

  mount.appendChild(
    el(
      "div",
      { class: "btn-row no-print" },
      el(
        "button",
        { class: "btn btn-primary", onClick: () => renderSetup(store, mount) },
        "New Quiz",
      ),
      el(
        "button",
        { class: "btn", onClick: () => window.print() },
        "Print / Save PDF",
      ),
      el(
        "button",
        { class: "btn", onClick: () => store.navigate("home") },
        "Back to Home",
      ),
    ),
  );
}
